/**
 * Copy y helpers de presentación para cierre de turno (multi-día incluido).
 */
import { formatLocalDate, formatLocalTime, VUELTO_DISPLAY_TZ } from '@shared/vueltoDateTime';
import type { CashPaymentMethodLine } from '../../infra/cashSummaryBreakdown';
import { formatTurnOpenDuration } from '../cashManagementUtils';
import { formatMoney, formatSalesCount } from './cashManagement.utils';

const WEEKDAY_SHORT = new Intl.DateTimeFormat('es-AR', {
  timeZone: VUELTO_DISPLAY_TZ,
  weekday: 'short',
});

function parseOpenedAt(openedAt: string): Date | null {
  const normalized =
    /^\d{4}-\d{2}-\d{2} /.test(openedAt) && !openedAt.includes('T')
      ? openedAt.replace(' ', 'T')
      : openedAt;
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Turno abierto en un día calendario anterior al de hoy (TZ producto). */
export function isTurnoOpenedOnPriorCalendarDay(openedAt: string, nowMs = Date.now()): boolean {
  const openedDay = formatLocalDate(openedAt);
  const todayDay = formatLocalDate(new Date(nowMs).toISOString());
  if (openedDay === '—' || todayDay === '—') return false;
  return openedDay !== todayDay;
}

/** Ej. «vie 28/08 08:42» para anclar turnos multi-día. */
export function formatTurnoOpenedAtShort(openedAt: string): string {
  const d = parseOpenedAt(openedAt);
  if (!d) return formatLocalDate(openedAt);
  const weekday = WEEKDAY_SHORT.format(d).replace(/\./g, '').trim();
  const datePart = formatLocalDate(openedAt);
  const [dd, mm] = datePart.split('/');
  const time = formatLocalTime(openedAt);
  if (!dd || !mm || time === '—') return formatLocalDate(openedAt);
  return `${weekday} ${dd}/${mm} ${time}`;
}

export function buildCloseTurnoHeroTitle(openedAt: string, nowMs = Date.now()): string {
  if (isTurnoOpenedOnPriorCalendarDay(openedAt, nowMs)) {
    return formatTurnOpenDuration(openedAt, nowMs);
  }
  const time = formatLocalTime(openedAt);
  return time === '—' ? 'Turno en curso' : `Turno desde ${time}`;
}

export function buildCloseTurnoHeroSubtitle(
  sessionSalesCount: number,
  sessionSalesTotal: number,
  multiDay = false
): string {
  const base =
    sessionSalesCount > 0
      ? `${formatSalesCount(sessionSalesCount)} · ${formatMoney(sessionSalesTotal)} facturados`
      : 'Sin ventas en este turno';
  return multiDay ? `${base} — el arqueo cubre todo el turno.` : `${base}.`;
}

export function formatTurnoResumenTitle(openedAt: string | null | undefined, nowMs = Date.now()): string {
  if (openedAt && isTurnoOpenedOnPriorCalendarDay(openedAt, nowMs)) {
    return 'Resumen del turno (abierto hace días)';
  }
  return 'Resumen del turno';
}

export function formatDayContextSectionTitle(openedAt: string | null | undefined, nowMs = Date.now()): string {
  if (openedAt && isTurnoOpenedOnPriorCalendarDay(openedAt, nowMs)) {
    return 'Solo hoy (referencia)';
  }
  return 'Contexto del día';
}

export function formatTodayContextNote(
  todaySalesCount: number,
  todaySalesTotal: number
): string | null {
  if (todaySalesCount <= 0) return null;
  return `Hoy facturaste ${formatMoney(todaySalesTotal)} (${formatSalesCount(todaySalesCount)}).`;
}

export function getNonCashSessionPaymentLines(
  breakdown: CashPaymentMethodLine[]
): CashPaymentMethodLine[] {
  return breakdown.filter((line) => line.methodId !== 'cash' && line.total > 0);
}
